import { Alert, Button, DialogContent, DialogTitle, Divider, FormControl, FormLabel, Modal, ModalClose, ModalDialog, ModalOverflow, Select, Snackbar, Option, Typography } from "@mui/joy"
import { useEffect, useState } from "react";
import { useAuth } from "../../context/authContext";
import { Report } from '@mui/icons-material';

import LoadingSpinnerComponent from "../LoadingSpinnerComponent";
import { Reply } from "./ReplyKitPopup";

const replykitNetworking = require('../../Networking/Reply Kit/ReplykitAPI');

export type Automation = {
    id: string;
    name: string;
    created_at: Date;
    is_active: boolean;
}

function CreateAutomationPopup({ subscription, app, didSaveNewAutomation, showAddNewAutomationModal, onClose }: any) {
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showError, setShowError] = useState(false);
    const [success, setSuccess] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [predefinedReplies, setPredefinedReplies] = useState<Reply[]>([]);
    const [languages, setLanguages] = useState<any[]>([]);

    useEffect(() => {
        if (subscription.plan === 'AAA' || subscription.plan === 'U') {
            getPredefinedReplies();
            getLanguages();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscription]);

    const createNewAutomation = (event: React.FormEvent<HTMLFormElement>) => {
        setLoading(true);

        event.preventDefault();

        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries(formData.entries());

        const rules = [{
            rule_type: "rating",
            operator: "equals",
            value: formJson.rating
        }, {
            rule_type: "language",
            operator: "equals",
            value: formJson.countryCode
        }]

        const actions = [{
            action_type: "template",
            reply_id: formJson.replyTemplate
        }]

        const automationData = {
            name: `Automation for ${formJson.rating} stars in ${formJson.countryCode}`,
            is_active: true,
            rules: rules,
            actions: actions
        }

        replykitNetworking.createAutomationOnKit(
            app.app.id,
            token,
            automationData
        ).then((automation: Automation) => {
            setLoading(false);
            setSuccess("Automation created successfully.");
            setShowSuccess(true);
            didSaveNewAutomation();
            onClose();
        }).catch((error: Error) => {
            setLoading(false);
            setError("Failed to create automation.");
            setShowError(true);
        });
    }

    const getLanguages = () => {
        // Fetch supported languages if needed
        setLoading(true);
        replykitNetworking.getLanguages(token).then((languages: string[]) => {
            // Handle languages if needed
            setLanguages(languages);
            setLoading(false);
        }).catch((error: Error) => {
            setError("Failed to fetch languages.");
            setShowError(true);
            setLoading(false);
        });
    }

    const getPredefinedReplies = () => {
        setLoading(true);
        replykitNetworking.getRepliesOnKit(app.app.id, token).then((replies: Reply[]) => {
            setPredefinedReplies(replies.filter((reply: Reply) => reply.is_active === true));
            setLoading(false);
        }).catch((error: Error) => {
            setError("Failed to fetch predefined replies.");
            setShowError(true);
            setLoading(false);
        });
    }

    return (
        <>
            {/* Added new reply template modal */}
            <>
                {showError &&
                    <Snackbar
                        variant="soft"
                        color="danger"
                        open={showError}
                        onClose={() => setShowError(false)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        startDecorator={<Report />}
                        endDecorator={
                            <Button
                                onClick={() => setShowError(false)}
                                size="sm"
                                variant="soft"
                                color="danger"
                            >
                                Dismiss
                            </Button>
                        }
                    >
                        {error}
                    </Snackbar>
                }
                {showSuccess &&
                    <Snackbar
                        variant="soft"
                        color="success"
                        open={showSuccess}
                        onClose={() => setShowSuccess(false)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        startDecorator={<Report />}
                        endDecorator={
                            <Button
                                onClick={() => setShowSuccess(false)}
                                size="sm"
                                variant="soft"
                                color="success"
                            >
                                Dismiss
                            </Button>
                        }
                    >
                        {success}
                    </Snackbar>
                }
            </>
            < Modal
                open={showAddNewAutomationModal}
                onClose={() => onClose()}>
                <ModalOverflow>
                    <ModalDialog
                        color="neutral"
                        layout="center"
                        size="sm"
                        variant="plain"
                        maxWidth={700}>
                        <ModalClose />
                        <DialogTitle>
                            Create New Automation
                        </DialogTitle>
                        <Divider />
                        <Typography
                            level="body-sm">
                            Setup a new automation to automatically respond to user reviews based on specific rules.
                        </Typography>
                        <Divider />
                        {loading ?
                            <LoadingSpinnerComponent />
                            :
                            <DialogContent>
                                {/* Form fields for new reply template would go here */}
                                <form
                                    onSubmit={createNewAutomation}
                                    className="mt-2">
                                    {predefinedReplies.length === 0 ?
                                        <Alert color="warning">
                                            You have no Reply Kit templates yet. Please create some in the App Settings to use predefined replies.
                                        </Alert>
                                        :
                                        <>
                                            <FormControl>
                                                <FormLabel
                                                    sx={{ fontSize: '12px', marginBottom: 1 }}>
                                                    Choose a reply from your Reply Kit
                                                </FormLabel>
                                                <Select
                                                    required
                                                    name='replyTemplate'
                                                    color="neutral"
                                                    size="lg"
                                                    variant="outlined"
                                                    sx={{ fontSize: '14px' }}>
                                                    {
                                                        predefinedReplies.map((reply: any) => (
                                                            <Option
                                                                key={reply.id}
                                                                value={reply.id}
                                                                sx={{ fontSize: '14px', maxWidth: '700px', background: '#ffffff' }}>
                                                                {reply.category} | {reply.language}
                                                            </Option>
                                                        ))
                                                    }
                                                </Select>
                                            </FormControl>
                                            <FormControl sx={{ marginTop: 2 /* margin top */ }}>
                                                <FormLabel
                                                    sx={{ fontSize: '12px', marginBottom: 1 }}>
                                                    Select Rating
                                                </FormLabel>
                                                <Select
                                                    required
                                                    name='rating'
                                                    color="neutral"
                                                    size="lg"
                                                    variant="outlined"
                                                    sx={{ fontSize: '14px' }}>
                                                    <Option
                                                        value="0"
                                                        sx={{ fontSize: '12px', background: '#ffffff' }}>
                                                        All Ratings
                                                    </Option>
                                                    <Option
                                                        value="1"
                                                        sx={{ fontSize: '12px', background: '#ffffff' }}>
                                                        ⭐️
                                                    </Option>
                                                    <Option
                                                        value="2"
                                                        sx={{ fontSize: '12px', background: '#ffffff' }}>
                                                        ⭐️ ⭐️
                                                    </Option>
                                                    <Option
                                                        value="3"
                                                        sx={{ fontSize: '12px', background: '#ffffff' }}>
                                                        ⭐️ ⭐️ ⭐️
                                                    </Option>
                                                    <Option
                                                        value="4"
                                                        sx={{ fontSize: '12px', background: '#ffffff' }}>
                                                        ⭐️ ⭐️ ⭐️ ⭐️
                                                    </Option>
                                                    <Option
                                                        value="5"
                                                        sx={{ fontSize: '12px', background: '#ffffff' }}>
                                                        ⭐️ ⭐️ ⭐️ ⭐️ ⭐️
                                                    </Option>
                                                </Select>
                                            </FormControl>
                                            <FormControl sx={{ marginTop: 2 }}>
                                                <FormLabel
                                                    sx={{ fontSize: '12px', marginBottom: 1 }}>
                                                    Choose a language
                                                </FormLabel>
                                                <Select
                                                    required
                                                    name='countryCode'
                                                    color="neutral"
                                                    size="lg"
                                                    variant="outlined"
                                                    sx={{ fontSize: '14px' }}>
                                                    {
                                                        languages.map((lang: any) => (
                                                            <Option
                                                                key={lang.id}
                                                                value={lang.iso_639_2}
                                                                sx={{ fontSize: '14px', maxWidth: '700px', background: '#ffffff' }}>
                                                                {lang.name_en}
                                                            </Option>
                                                        ))
                                                    }
                                                </Select>
                                            </FormControl>
                                            <FormLabel
                                                sx={{ fontSize: '12px', marginTop: 4, color: '#666666' }}>
                                                All reviews with the selected rating and language will receive this reply.
                                            </FormLabel>
                                        </>
                                    }
                                    <Button
                                        type="submit"
                                        sx={{ mt: 4 /* margin top */ }}>
                                        Save Automation
                                    </Button>
                                </form>
                            </DialogContent>
                        }
                    </ModalDialog>
                </ModalOverflow>
            </Modal >
        </>
    );
}

export default CreateAutomationPopup;